import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Divider,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    CircularProgress,
    Chip
} from "@mui/material";
import { useMemo } from "react";
import { Edit as EditIcon } from "@mui/icons-material";
import { formatarMonetárioParaNúmero, formatarValorMonetario } from "../../../../../../../helpers/masks";

export default function DialogConfirmarOrdens({
    open,
    fornecedoresProcessados,
    onConfirmar,
    onEditar,
    onCancelar,
    isLoading
}: DialogConfirmarOrdensProps) {

    const dadosFornecedores = useMemo(() => {
        return fornecedoresProcessados.map(fornecedor => {
            const valorProdutos = fornecedor.produtos.reduce((acc, item) => 
                acc + (Number(item.valor_unitario - formatarMonetárioParaNúmero(item.valor_desconto || '0') + formatarMonetárioParaNúmero(item.valor_acrescimo || '0')) * item.quantidade), 
                0
            );

            const valorFrete = fornecedor.frete ?? 0;
            const descontoFrete = formatarMonetárioParaNúmero(fornecedor.descontoFrete || '0');
            const total = valorProdutos + valorFrete - descontoFrete;
            return {
                fornecedor,
                valorProdutos,
                valorFrete,
                descontoFrete,
                total
            };
        });
    }, [fornecedoresProcessados]);

    return (
        <Dialog
            open={open}
            maxWidth="md"
            fullWidth
            onClose={!isLoading ? onCancelar : undefined}
        >
            <DialogTitle>
                Confirmação de criação de ordens de compra
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Você está prestes a criar {fornecedoresProcessados.length} {fornecedoresProcessados.length === 1 ? 'ordem' : 'ordens'} de compra. Revise as informações abaixo:
                    </Typography>
                </Box>

                {dadosFornecedores.map((dados, index) => {
                    const { fornecedor, valorProdutos, valorFrete, descontoFrete, total } = dados;

                    return (
                        <Paper 
                            key={index} 
                            elevation={2} 
                            sx={{ 
                                mb: 2, 
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider'
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Box>
                                    <Typography variant="h6" gutterBottom>
                                        Ordem {index + 1}: {fornecedor.fornecedor}
                                    </Typography>
                                    <Chip 
                                        label={`${fornecedor.produtos.length} ${fornecedor.produtos.length === 1 ? 'produto' : 'produtos'}`} 
                                        size="small" 
                                        color="primary" 
                                        variant="outlined"
                                    />
                                </Box>
                                <IconButton 
                                    size="small" 
                                    onClick={() => onEditar(index)}
                                    disabled={isLoading}
                                >
                                    <EditIcon />
                                </IconButton>
                            </Box>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Produto</TableCell>
                                            <TableCell align="right">Qtd</TableCell>
                                            <TableCell align="right">Valor Unit.</TableCell>
                                            <TableCell align="right">Total</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {fornecedor.produtos.map((produto, idx) => {
                                            const valorUnitarioFinal = Number(produto.valor_unitario) - 
                                                formatarMonetárioParaNúmero(produto.valor_desconto || '0') + 
                                                formatarMonetárioParaNúmero(produto.valor_acrescimo || '0');
                                            const totalProduto = valorUnitarioFinal * produto.quantidade;

                                            return (
                                                <TableRow key={idx}>
                                                    <TableCell>{produto.nome}</TableCell>
                                                    <TableCell align="right">{produto.quantidade}</TableCell>
                                                    <TableCell align="right">{formatarValorMonetario(valorUnitarioFinal)}</TableCell>
                                                    <TableCell align="right">{formatarValorMonetario(totalProduto)}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2">Subtotal Produtos:</Typography>
                                    <Typography variant="body2">{formatarValorMonetario(valorProdutos)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2">Frete:</Typography>
                                    <Typography variant="body2">{formatarValorMonetario(valorFrete - descontoFrete)}</Typography>
                                </Box>
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="subtitle1" fontWeight="bold">Total:</Typography>
                                    <Typography variant="subtitle1" fontWeight="bold">{formatarValorMonetario(total)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Parcelamento: {fornecedor.parcelamento.valores.length}x
                                    </Typography>
                                    {fornecedor.parcelamento.valores.length > 0 && (
                                        <Typography variant="body2" color="text.secondary">
                                            Primeira: {formatarValorMonetario(fornecedor.parcelamento.valores[0])}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Paper>
                    );
                })}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button 
                    onClick={onCancelar}
                    disabled={isLoading}
                    variant="outlined"
                >
                    Cancelar
                </Button>
                <Button 
                    onClick={onConfirmar}
                    variant="contained"
                    color="primary"
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={20} /> : null}
                >
                    {isLoading ? 'Criando...' : 'Confirmar e Criar Ordens'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

interface DialogConfirmarOrdensProps {
    open: boolean;
    fornecedoresProcessados: FornecedorProcessado[];
    onConfirmar: () => void;
    onEditar: (index: number) => void;
    onCancelar: () => void;
    isLoading: boolean;
}

interface FornecedorProcessado {
    fornecedor: string;
    produtos: any[];
    descontoFrete: string;
    frete: number;
    parcelamento: {
        parcelas: any[];
        valores: number[];
        vencimentos: string[];
        forma_primeira_parcela?: 'personalizada' | 'dias_corridos';
        dias_corridos_primeira_parcela?: string;
    };
}
